import { children, createEffect, createSignal, For, onCleanup, Show, splitProps } from 'solid-js'
import Icon from '~/components/Icon'
import type { InfiniteListProps } from '~/types/InfiniteList'

const PULL_THRESHOLD = 70

/** Busca el ancestro scrolleable (el `overflow-y-auto` del layout admin). */
function findScrollParent(node: HTMLElement | undefined): HTMLElement | null {
  let el = node?.parentElement ?? null
  while (el) {
    const overflowY = getComputedStyle(el).overflowY
    if (overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay') {
      return el
    }
    el = el.parentElement
  }
  return null
}

/**
 * Lista con scroll infinito y pull-to-refresh, reutilizable en cualquier listado
 * del panel (servicios, clientes, prospectos…).
 *
 * - Al llegar al final dispara `onLoadMore` (scroll-end) mediante un
 *   IntersectionObserver en un sentinela.
 * - Desde el tope, tirar hacia abajo dispara `onRefresh` (pull-to-refresh).
 * - `renderItem` recibe cada ítem, así que sirve tanto para tarjetas con
 *   `SwipeAction` como para `div` simples.
 */
function InfiniteList<T>(props: InfiniteListProps<T>) {
  const [local, others] = splitProps(props, ['items', 'renderItem', 'keyField', 'hasMore', 'loading', 'onLoadMore', 'onRefresh', 'pullToRefresh', 'emptyMessage', 'endMessage', 'pullLabel', 'class'])

  const [pullDistance, setPullDistance] = createSignal(0)
  const [refreshing, setRefreshing] = createSignal(false)

  let rootRef: HTMLDivElement | undefined
  const [sentinelNode, setSentinelNode] = createSignal<HTMLDivElement>()

  // ── Scroll infinito: observa el sentinela del final ──────────────────────
  // El scroll real vive en un ancestro (el `overflow-y-auto` del layout admin),
  // así que se observa contra ese contenedor y no contra el viewport.
  createEffect(() => {
    const node = sentinelNode()
    if (!node) return
    const io = new IntersectionObserver(
      entries => {
        if (entries[0]?.isIntersecting) local.onLoadMore()
      },
      { root: findScrollParent(node), rootMargin: '200px' }
    )
    io.observe(node)
    onCleanup(() => io.disconnect())
  })

  // ── Pull-to-refresh: gesto de tirar desde el tope ────────────────────────
  let startY = 0
  let pulling = false
  let scrollParent: HTMLElement | null = null

  const onTouchStart = (e: TouchEvent) => {
    if (!local.pullToRefresh || !local.onRefresh) return
    scrollParent = findScrollParent(rootRef)
    pulling = true
    startY = e.touches[0].clientY
    setPullDistance(0)
  }

  const onTouchMove = (e: TouchEvent) => {
    if (!local.pullToRefresh || !local.onRefresh || !pulling) return
    if (refreshing()) return
    if (scrollParent && scrollParent.scrollTop > 0) return
    const dy = e.touches[0].clientY - startY
    if (dy > 0) {
      setPullDistance(Math.min(90, dy * 0.5))
      // Evita que el navegador haga su propio overscroll en iOS.
      if (scrollParent && scrollParent.scrollTop === 0 && dy > 0) e.preventDefault()
    }
  }

  const onTouchEnd = async () => {
    if (!local.pullToRefresh || !local.onRefresh || !pulling) return
    pulling = false
    if (pullDistance() >= PULL_THRESHOLD) {
      setRefreshing(true)
      setPullDistance(40)
      try {
        await local.onRefresh()
      } finally {
        setRefreshing(false)
        setPullDistance(0)
      }
    } else {
      setPullDistance(0)
    }
  }

  const resolvedEmpty = children(() => local.emptyMessage)

  return (
    <div
      ref={rootRef}
      class={local.class}
      style={pullDistance() > 0 ? { transform: `translateY(${pullDistance()}px)`, transition: pulling ? 'none' : 'transform 0.25s ease' } : undefined}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
      {...others}
    >
      {/* Indicador de pull-to-refresh */}
      <Show when={local.pullToRefresh && (pullDistance() > 0 || refreshing())}>
        <div class="flex items-center justify-center gap-2 py-2 text-sm text-foreground-subtle">
          <Show when={refreshing()} fallback={<Icon icon="material-symbols-light:arrow-downward" size={18} class="text-foreground-subtle" />}>
            <Icon icon="svg-spinners:ring-resize" size={18} class="text-foreground-subtle" />
          </Show>
          <span>{refreshing() ? 'Actualizando…' : (local.pullLabel ?? 'Suelta para actualizar')}</span>
        </div>
      </Show>

      <For each={local.items} fallback={resolvedEmpty()}>
        {(item, index) => <div data-key={local.keyField(item)}>{local.renderItem(item, index())}</div>}
      </For>

      {/* Sentinela: dispara onLoadMore cuando entra en la vista */}
      <Show when={local.hasMore}>
        <div class="flex items-center justify-center py-3">
          <Show when={local.loading} fallback={<span class="text-sm text-foreground-subtle">Cargando más…</span>}>
            <Icon icon="svg-spinners:ring-resize" size={22} class="text-foreground-subtle" />
          </Show>
        </div>
      </Show>

      <Show when={!local.hasMore && local.endMessage}>
        <div class="py-3 text-center text-sm text-foreground-subtle">{local.endMessage}</div>
      </Show>

      <div ref={setSentinelNode} />
    </div>
  )
}

export default InfiniteList
